import { apiGet, apiPost, apiDelete, handleApiError, showErrorToast, apiClient } from './apiClient';
import { Vehicle, VehicleWithDriver } from '../models/vehicle';
import { Considerations, defaultConsiderations } from '../models/considerations';
import { PotentialOffersFilter } from '../models/potentialOffersFilter';
import { SearchFilterMode } from '../models/vehicleAutoSearch';
import { toast } from '../utils/toast';
import { PotentialOffer } from '../types';
import { t } from 'i18next';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Options for fetching vehicles from the API.
 */
export interface FetchVehiclesOptions {
  latitude?: number;
  longitude?: number;
  radius?: number;
  preferred_country?: string;
  forUser?: boolean;
  emptyAt?: number;
  offset?: number;
  limit?: number;
  geojson?: boolean;
  with_drivers?: boolean;
  with_current_booking?: boolean;
  include_inactive?: boolean;
}

/**
 * Vehicle creation/update payload interface
 * Based on backend VEHICLE_SCHEMA
 */
export type VehiclePayload = Partial<Omit<Vehicle, keyof {
  emptyAt: string
}> & {
  emptyAt: number; // timestamp
}>;

/**
 * Vehicle API Functions
 */

/**
 * Fetch vehicles from the API with optional filters
 */
export const fetchVehicles = async (options: FetchVehiclesOptions = {}): Promise<(Vehicle | VehicleWithDriver)[]> => {
  try {
    // Build query parameters
    const queryParams: Record<string, string | number | boolean> = {
      offset: options.offset ?? 0,
      limit: options.limit ?? 1000,
    };

    // Add optional parameters if provided
    if (options.latitude !== undefined) queryParams.latitude = options.latitude;
    if (options.longitude !== undefined) queryParams.longitude = options.longitude;
    if (options.radius !== undefined) queryParams.radius = options.radius;
    if (options.preferred_country !== undefined) queryParams.preferred_country = options.preferred_country;
    if (options.forUser !== undefined) queryParams.forUser = options.forUser;
    if (options.emptyAt !== undefined) queryParams.emptyAt = options.emptyAt;
    if (options.geojson !== undefined) queryParams.geojson = options.geojson;
    if (options.with_drivers !== undefined) queryParams.with_drivers = options.with_drivers;
    if (options.with_current_booking !== undefined) queryParams.with_current_booking = options.with_current_booking;
    if (options.include_inactive !== undefined) queryParams.include_inactive = options.include_inactive;

    const response = await apiGet(`${API_BASE_URL}/api/v1/vehicles`, queryParams);
    const data = await response.json();
    return data.result || [];
  } catch (error) {
    handleApiError(error, t('Failed to fetch vehicles'));
    throw error;
  }
};

/**
 * Delete a vehicle by ID
 */
export const deleteVehicle = async (vehicleId: number): Promise<void> => {
  const response = await apiDelete(`${API_BASE_URL}/api/v1/vehicle/${vehicleId}/delete`);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed with status ${response.status}: ${errorText}`);
  }
};

/**
 * Import vehicles from external source (Cartrack)
 */
export const importVehiclesFromCartrack = async (): Promise<void> => {
  await apiPost(`${API_BASE_URL}/api/v1/vehicle/import/tracker`, {});
};

/**
 * Fetch a single vehicle by ID
 */
export const getVehicle = async (vehicleId: number): Promise<Vehicle> => {
  try {
    const response = await apiGet(`${API_BASE_URL}/api/v1/vehicle/${vehicleId}`);
    const data = await response.json();
    return data.result;
  } catch (error) {
    handleApiError(error, 'Failed to fetch vehicle');
    throw error;
  }
};

/**
 * Create a new vehicle
 */
export const createVehicle = async (vehicleData: VehiclePayload): Promise<void> => {
  try {
    await apiPost(`${API_BASE_URL}/api/v1/vehicle/create`, vehicleData);
  } catch (error) {
    handleApiError(error, 'Failed to create vehicle');
    throw error;
  }
};

/**
 * Update an existing vehicle
 */
export const updateVehicle = async (vehicleId: number, vehicleData: Partial<VehiclePayload>): Promise<Vehicle | void> => {
  try {
    const response = await apiPost(`${API_BASE_URL}/api/v1/vehicle/${vehicleId}/update`, vehicleData);
    // Handle 204 No Content response
    if (response.status === 204) {
      return; // No content to return
    }

    const data = await response.json();
    return data.result;
  } catch (error) {
    showErrorToast(error, 'Failed to update vehicle');
    throw error;
  }
};

export const fetchVehicleNegotiatedOffers = async (
  vehicleId: number,
  considerations?: Considerations,
  offset: number = 0,
  limit: number = 250,
  orderBy?: string,
  orderAsc?: 'ASC' | 'DESC',
  filters?: PotentialOffersFilter,
): Promise<{
  offers: PotentialOffer[];
  count: number;
  negotiationsStats?: {
    unreadEmails: number;
    needHuman: number;
    waitingForClient: number;
  };
}> => {
  try {
    // Use provided considerations or fall back to stored ones from context
    let activeConsiderations: Considerations;

    if (considerations) {
      activeConsiderations = considerations;
    } else {
      try {
        const stored = localStorage.getItem('offerConsiderations');
        activeConsiderations = stored ? JSON.parse(stored) : defaultConsiderations;
      } catch (error) {
        console.warn('Failed to parse stored considerations:', error);
        activeConsiderations = defaultConsiderations;
      }
    }

    const queryParams: Record<string, string | number | boolean> = {
      epoch: Date.now(),
      offset,
      limit,
    };

    // Add considerations to query params
    Object.entries(activeConsiderations).forEach(([key, value]) => {
      queryParams[`considerations[${key}]`] = value as boolean;
    });

    // Add filters to query params
    if (filters?.fullTruckLoad !== undefined) {
      queryParams.fullTruckLoad = filters.fullTruckLoad;
    }

    if (filters?.startTime !== undefined) {
      queryParams.start_time = filters.startTime instanceof Date
        ? filters.startTime.getTime()
        : typeof filters.startTime === 'number'
          ? filters.startTime
          : new Date(filters.startTime).getTime();
    }

    if (filters?.endTime !== undefined) {
      queryParams.end_time = filters.endTime instanceof Date
        ? filters.endTime.getTime()
        : typeof filters.endTime === 'number'
          ? filters.endTime
          : new Date(filters.endTime).getTime();
    }

    if (filters?.status !== undefined) {
      const status = Number(filters.status);
      if (!isNaN(status)) {
        queryParams.status = filters.status;
      }
    }

    if (filters?.originCountry !== undefined) {
      queryParams.originCountry = filters.originCountry;
    }
    if (filters?.destinationCountry !== undefined) {
      queryParams.destinationCountry = filters.destinationCountry;
    }
    if (filters?.contact !== undefined) {
      queryParams.contact = filters.contact;
    }

    if (orderBy) queryParams.orderColumn = orderBy;
    if (orderAsc) queryParams.orderAsc = orderAsc;

    const baseUrl = `${API_BASE_URL}/api/v1/vehicle/${vehicleId}/offers/negotiated`;

    const response = await apiGet(baseUrl, queryParams);
    const data = await response.json();
    return {
      offers: data.result || [],
      count: data.count || 0,
      negotiationsStats: data.negotiationsStats
    };
  } catch (error: any) {
    switch (error?.errorData?.error?.additionalInfo) {
      case 'client.extended.bookingNotGeocoded':
        toast.warn(t('Booking not geocoded for the selected vehicle'));
        break;

      case 'client.extended.noRecentBookings':
        toast.warn(t('No recent bookings for this vehicle for the selected vehicle'));
        break;

      default:
        toast.error(t('Failed to fetch vehicle offers for the selected vehicle'));
        break;
    }
    throw error;
  }
};

/**
 * Lightweight fetch of just the negotiationsStats for a single vehicle.
 * Returns `null` on failure so background pollers can use it without
 * spamming the user with error toasts.
 */
export const fetchVehicleNegotiationStats = async (
  vehicleId: number,
): Promise<{ unreadEmails: number; needHuman: number; waitingForClient: number } | null> => {
  try {
    const response = await apiGet(
      `${API_BASE_URL}/api/v1/vehicle/${vehicleId}/offers/negotiated`,
      { epoch: Date.now(), offset: 0, limit: 1 },
    );
    const data = await response.json();
    const stats = data?.negotiationsStats;
    if (!stats) return null;
    return {
      unreadEmails: Number(stats.unreadEmails) || 0,
      needHuman: Number(stats.needHuman) || 0,
      waitingForClient: Number(stats.waitingForClient) || 0,
    };
  } catch (_err) {
    return null;
  }
};

export const fetchContacts = async (
  s: string,
): Promise<string[]> => {
  try {
    const response = await apiGet(`${API_BASE_URL}/api/v1/offers/contacts`, { s: s });
    const data = await response.json();
    return data.result;
  } catch (error: any) {
    toast.warn(t('Failed to fetch contacts'));
    throw error;
  }
};

/**
 * Fetch vehicle offers with optional considerations filter
 * If considerations are not provided, will use stored considerations from localStorage
 */
export const fetchVehicleOffers = async (
  vehicleId: number,
  considerations?: Considerations,
  filterMode: SearchFilterMode = 'default',
  offset: number = 0,
  limit: number = 250,
  orderBy?: string,
  orderAsc?: 'ASC' | 'DESC',
  includeOngoingNegos?: 'INCLUDE' | 'EXCLUDE' | 'ONLY',
  offerPattern?: string,
  applyExcludedDestinations?: boolean,
  selectedCountries?: string[],
  filters?: PotentialOffersFilter,
  maxWeightT?: number,
  maxLengthM?: number,
): Promise<{
  offers: PotentialOffer[];
  count: number;
  negotiationsStats?: {
    unreadEmails: number;
    needHuman: number;
    waitingForClient: number;
  };
}> => {
  try {
    // Use provided considerations or fall back to stored ones from context
    let activeConsiderations: Considerations;

    if (considerations) {
      activeConsiderations = considerations;
    } else {
      try {
        const stored = localStorage.getItem('offerConsiderations');
        activeConsiderations = stored ? JSON.parse(stored) : defaultConsiderations;
      } catch (error) {
        console.warn('Failed to parse stored considerations:', error);
        activeConsiderations = defaultConsiderations;
      }
    }

    const queryParams: Record<string, string | number | boolean> = {
      epoch: Date.now(),
      offset,
      limit,
    };

    // Add considerations to query params
    Object.entries(activeConsiderations).forEach(([key, value]) => {
      queryParams[`considerations[${key}]`] = value as boolean;
    });

    // Add filters to query params
    if (filters?.fullTruckLoad !== undefined) {
      queryParams.fullTruckLoad = filters.fullTruckLoad;
    }

    if (filters?.startTime !== undefined) {
      queryParams.start_time = filters.startTime instanceof Date
        ? filters.startTime.getTime()
        : typeof filters.startTime === 'number'
          ? filters.startTime
          : new Date(filters.startTime).getTime();
    }

    if (filters?.endTime !== undefined) {
      queryParams.end_time = filters.endTime instanceof Date
        ? filters.endTime.getTime()
        : typeof filters.endTime === 'number'
          ? filters.endTime
          : new Date(filters.endTime).getTime();
    }

    // Add filter mode to query params only if not default
    if (filterMode !== 'default') {
      queryParams.filterMode = filterMode;
    }

    if (orderBy) queryParams.orderColumn = orderBy;
    if (orderAsc) queryParams.orderAsc = orderAsc;

    if (includeOngoingNegos !== undefined) {
      queryParams.includeOngoingNegos = includeOngoingNegos;
    }

    if (offerPattern !== undefined) {
      queryParams.offerPattern = offerPattern;
    }

    if (applyExcludedDestinations !== undefined) {
      queryParams.applyExcludedDestinations = applyExcludedDestinations;
    }

    if (maxWeightT !== undefined) {
      queryParams.maxWeightT = maxWeightT;
    }

    if (maxLengthM !== undefined) {
      queryParams.maxLengthM = maxLengthM;
    }


    // Handle selected countries as repeated query parameters
    const baseUrl = `${API_BASE_URL}/api/v1/vehicle/${vehicleId}/offers`;
    if (filterMode === 'selectedCountries' && selectedCountries && selectedCountries.length > 0) {
      const validCountries = selectedCountries.filter(c => c && c.trim() !== '');
      if (validCountries.length > 0) {
        // Build complete URL with all query parameters and repeated country parameters
        const urlParams = new URLSearchParams(queryParams as any);
        validCountries.forEach(country => {
          urlParams.append('selected_countries', country);
        });
        const fullUrl = `${baseUrl}?${urlParams.toString()}`;
        const response = await apiClient(fullUrl, { method: 'GET' });
        const data = await response.json();
        return {
          offers: data.result || [],
          count: data.count || 0,
          negotiationsStats: data.negotiationsStats
        };
      }
    }

    const response = await apiGet(baseUrl, queryParams);
    const data = await response.json();
    return {
      offers: data.result || [],
      count: data.count || 0,
      negotiationsStats: data.negotiationsStats
    };
  } catch (error: any) {
    switch (error?.errorData?.error?.additionalInfo) {
      case 'client.extended.bookingNotGeocoded':
        toast.warn(t('Booking not geocoded for the selected vehicle'));
        break;

      case 'client.extended.noRecentBookings':
        switch (includeOngoingNegos) {
          case 'ONLY':
            toast.warn(t('No recent bookings for this vehicle for the selected vehicle in mode "Ongoing Negotiations"'));
            break;
          case 'EXCLUDE':
            toast.warn(t('No recent bookings for this vehicle for the selected vehicle in mode "Exclude Ongoing Negotiations"'));
            break;
          default:
            toast.warn(t('No recent bookings for this vehicle for the selected vehicle in mode "All"'));
            break;
        }

        break;

      default:
        toast.error(t('Failed to fetch vehicle offers for the selected vehicle'));
        break;
    }
    throw error;
  }
};
